import type { TimezoneOption } from '@/utils/timezone';

import Iconify from '@/components/Iconify';
import { getSelectedTimezone, setSelectedTimezone, TIMEZONE_OPTIONS } from '@/utils/timezone';
import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

export interface TimezoneSelectorProps {
  onChange?: (timezone: TimezoneOption) => void;
}

export const TimezoneSelector = ({ onChange }: TimezoneSelectorProps) => {
  const [timezone, setTimezone] = useState<TimezoneOption>(getSelectedTimezone);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const stored = getSelectedTimezone();
    setTimezone(stored);
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (selected: TimezoneOption) => {
    setTimezone(selected);
    setSelectedTimezone(selected);
    onChange?.(selected);
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={`Timezone: ${timezone.label}`} arrow placement="bottom">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              {timezone.value}
            </Typography>
            <Typography variant="caption" sx={{ mr: 1 }}>
              ({timezone.shortLabel})
            </Typography>
          </Box>

          <IconButton onClick={handleOpen} size="medium" aria-label="Select timezone">
            <Iconify icon="tabler:timezone" width={35} />
          </IconButton>
        </Box>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{ sx: { minWidth: 260 } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Iconify icon="tabler:timezone" width={18} />
          <Box>
            <Typography variant="subtitle2">Select Timezone</Typography>
          </Box>
        </Box>

        <Divider />

        <MenuList sx={{ py: 0 }}>
          {TIMEZONE_OPTIONS.map((tz) => {
            const selected = timezone.value === tz.value;
            return (
              <MenuItem key={tz.value} onClick={() => handleSelect(tz)} selected={selected}>
                <ListItemText
                  primary={<Typography variant="body2">{tz.label}</Typography>}
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {tz.value}
                    </Typography>
                  }
                />

                <ListItemIcon sx={{ minWidth: 36 }}>
                  {selected ? <Iconify icon="mdi:check" width={18} /> : <Box sx={{ width: 18 }} />}
                </ListItemIcon>
              </MenuItem>
            );
          })}
        </MenuList>
      </Popover>
    </>
  );
};
